const DB_NAME = "ajfarm-offline-sync-v1";
const DB_VERSION = 2;
const DEVICE_KEY = "ajfarm:offline-device-id:v1";
const ACTIVE_USER_KEY = "ajfarm:offline-active-user:v1";
const LEGACY_PENDING_KEY = "ajfarm:field-pending:v2";
const OFFLINE_SESSION_NAME = "authenticated-session";
const OFFLINE_SESSION_MAX_AGE_MS = 8 * 60 * 60 * 1000;
const SAFE_STATUSES = new Set(["pending", "syncing", "retrying", "failed", "review_required", "synced"]);
const activeSyncs = new Map();
let observedOnline = typeof navigator === "undefined" ? true : navigator.onLine;
if (typeof window !== "undefined") {
  window.addEventListener("online", () => { observedOnline = true; });
  window.addEventListener("offline", () => { observedOnline = false; });
}

export function isNetworkAvailable() {
  return observedOnline;
}

export const ONLINE_ONLY_PREFIXES = [
  "/payroll", "/rewards", "/admin", "/auth", "/users", "/roles",
  "/financial", "/settings", "/payments", "/invoices", "/mortality-records",
];

export function isOfflineAllowedEndpoint(endpoint) {
  const value = String(endpoint || "").trim();
  if (!value.startsWith("/") || value.includes("?") || value.includes("#")) return false;
  if (ONLINE_ONLY_PREFIXES.some((prefix) => value === prefix || value.startsWith(`${prefix}/`))) return false;
  return [
    "/feeding-records", "/water-checks", "/health-inspections", "/treatments",
    "/vaccination-records", "/weight-records", "/breeding-records",
    "/pregnancy-checks", "/farrowing-records", "/crop-plots",
    "/crop-activities", "/crop-harvests", "/inventory-usage", "/incidents",
    "/forms/submissions", "/attendance/clock",
  ].includes(value) || /^\/tasks\/[A-Za-z0-9._:-]{1,128}\/complete$/.test(value);
}

export function retryDelayMs(retryCount, random = Math.random) {
  const attempt = Math.max(0, Number(retryCount) || 0);
  const base = Math.min(1000 * (2 ** attempt), 5 * 60 * 1000);
  return Math.round(base * (0.8 + Math.max(0, Math.min(1, random())) * 0.4));
}

export function pendingAttendanceStatus(rows, initialStatus = "not_started") {
  const nextByAction = {
    shift_start: "shift_started", clock_in: "working", break_start: "on_break",
    break_end: "working", clock_out: "clocked_out", shift_end: "finished",
  };
  return (rows || [])
    .filter((row) => row.module === "attendance" && row.status !== "synced" && nextByAction[row.action])
    .sort((a, b) => String(a.queuedAt || a.localTimestamp).localeCompare(String(b.queuedAt || b.localTimestamp)))
    .reduce((status, row) => nextByAction[row.action] || status, initialStatus);
}

export function createOperation({ userId, deviceId, module, action, endpoint, payload, evidenceIds = [], serverKnownRevision = null, localTimestamp, operationId }) {
  if (!userId) throw new Error("Authenticated user is required for an offline operation");
  if (!isOfflineAllowedEndpoint(endpoint)) throw new Error("Internet connection required for this action");
  const id = operationId || globalThis.crypto?.randomUUID?.();
  if (!id) throw new Error("Secure operation identity is unavailable");
  const timestamp = localTimestamp || new Date().toISOString();
  return {
    operationId: id,
    userId: String(userId),
    deviceId: String(deviceId),
    module: String(module),
    action: String(action),
    endpoint: String(endpoint),
    localTimestamp: timestamp,
    serverKnownRevision: serverKnownRevision || null,
    payload: { ...(payload || {}), source_submission_id: id },
    evidenceIds: [...evidenceIds],
    status: "pending",
    retryCount: 0,
    errorCode: null,
    queuedAt: timestamp,
    nextRetryAt: 0,
    acknowledgedAt: null,
  };
}

export async function enqueueEvidence({ evidenceId, operationId, userId, file, capturedAt }) {
  if (!evidenceId || !operationId || !userId || !file) throw new Error("Invalid offline evidence");
  const db = await openDb();
  try {
    const key = await userKey(db, userId);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, await file.arrayBuffer());
    await requestResult(db.transaction("evidence", "readwrite").objectStore("evidence").put({
      evidenceId, operationId, userId: String(userId), name: String(file.name || "field-evidence"),
      type: String(file.type || "application/octet-stream"), size: Number(file.size || 0),
      capturedAt: capturedAt || new Date().toISOString(), status: "pending", retryCount: 0,
      iv: Array.from(iv), ciphertext,
    }));
    return evidenceId;
  } finally {
    db.close();
  }
}

async function uploadRequiredEvidence(row, key, uploadEvidence) {
  if (!(row.evidenceIds || []).length) return {};
  if (typeof uploadEvidence !== "function") throw new Error("Evidence uploader is required");
  const db = await openDb();
  const uploaded = {};
  try {
    for (const evidenceId of row.evidenceIds) {
      const tx = db.transaction("evidence", "readonly");
      const evidence = await requestResult(tx.objectStore("evidence").get(evidenceId));
      if (!evidence || evidence.userId !== row.userId || evidence.operationId !== row.operationId) {
        throw new Error("Required offline evidence is unavailable");
      }
      if (evidence.status === "synced" && evidence.serverFile) {
        uploaded[evidenceId] = evidence.serverFile;
        continue;
      }
      const bytes = await crypto.subtle.decrypt({ name: "AES-GCM", iv: new Uint8Array(evidence.iv) }, key, evidence.ciphertext);
      const file = new File([bytes], evidence.name, { type: evidence.type, lastModified: Date.parse(evidence.capturedAt) || Date.now() });
      const serverFile = await uploadEvidence(file, { evidenceId, operationId: row.operationId, capturedAt: evidence.capturedAt });
      evidence.status = "synced";
      evidence.serverFile = serverFile;
      evidence.acknowledgedAt = new Date().toISOString();
      await requestResult(db.transaction("evidence", "readwrite").objectStore("evidence").put(evidence));
      uploaded[evidenceId] = serverFile;
    }
    return uploaded;
  } finally {
    db.close();
  }
}

export function getDeviceId() {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = globalThis.crypto?.randomUUID?.();
    if (!id) throw new Error("Secure device identity is unavailable");
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

export async function registerOfflineUser(userId) {
  const current = localStorage.getItem(ACTIVE_USER_KEY);
  if (current && current !== String(userId)) await clearOfflineDataForUser(current);
  localStorage.setItem(ACTIVE_USER_KEY, String(userId));
  let legacy = [];
  try { legacy = JSON.parse(localStorage.getItem(LEGACY_PENDING_KEY) || "[]"); } catch { legacy = []; }
  if (Array.isArray(legacy)) {
    for (const row of legacy) {
      if (!row?.submissionId || !row?.endpoint || !row?.payload) continue;
      const allowed = isOfflineAllowedEndpoint(row.endpoint);
      const migrated = allowed
        ? createOperation({ userId, deviceId: getDeviceId(), module: row.action || "legacy_field", action: row.action || "create", endpoint: row.endpoint, payload: row.payload, operationId: row.submissionId, localTimestamp: row.queuedAt })
        : { operationId: row.submissionId, userId: String(userId), deviceId: getDeviceId(), module: row.action || "legacy_field", action: row.action || "create", endpoint: row.endpoint, localTimestamp: row.queuedAt || new Date().toISOString(), serverKnownRevision: null, payload: row.payload, evidenceIds: [], status: "review_required", retryCount: 0, errorCode: "LEGACY_ACTION_REQUIRES_REVIEW", queuedAt: row.queuedAt || new Date().toISOString(), nextRetryAt: 0, acknowledgedAt: null };
      await enqueueOperation(migrated);
    }
  }
  localStorage.removeItem(LEGACY_PENDING_KEY);
}

function openDb() {
  return new Promise((resolve, reject) => {
    if (!globalThis.indexedDB || !globalThis.crypto?.subtle) {
      reject(new Error("Secure offline storage is unavailable on this device"));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains("meta")) db.createObjectStore("meta", { keyPath: "id" });
      if (!db.objectStoreNames.contains("operations")) {
        const store = db.createObjectStore("operations", { keyPath: "operationId" });
        store.createIndex("userId", "userId", { unique: false });
        store.createIndex("userStatus", ["userId", "status"], { unique: false });
      }
      if (!db.objectStoreNames.contains("evidence")) {
        const store = db.createObjectStore("evidence", { keyPath: "evidenceId" });
        store.createIndex("userId", "userId", { unique: false });
      }
      if (!db.objectStoreNames.contains("references")) {
        const store = db.createObjectStore("references", { keyPath: "id" });
        store.createIndex("userId", "userId", { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("Offline storage could not be opened"));
  });
}

export async function saveOfflineReference(userId, name, value) {
  const db = await openDb();
  try {
    const key = await userKey(db, userId);
    const encrypted = await encryptJson(key, value);
    await requestResult(db.transaction("references", "readwrite").objectStore("references").put({
      id: `${userId}:${name}`, userId: String(userId), name, savedAt: Date.now(), ...encrypted,
    }));
    return value;
  } finally { db.close(); }
}

export async function loadOfflineReference(userId, name) {
  const db = await openDb();
  try {
    const key = await userKey(db, userId);
    const row = await requestResult(db.transaction("references", "readonly").objectStore("references").get(`${userId}:${name}`));
    if (!row) return null;
    return { data: await decryptJson(key, row), savedAt: row.savedAt };
  } finally { db.close(); }
}

export async function secureCachedFetch(userId, name, fetcher) {
  if (!userId) throw new Error("Authenticated user is required for offline reference data");
  try {
    const data = await fetcher();
    await saveOfflineReference(userId, name, data);
    return { data, fromCache: false, savedAt: Date.now() };
  } catch (error) {
    const cached = await loadOfflineReference(userId, name);
    if (!cached) throw error;
    return { ...cached, fromCache: true };
  }
}

export async function saveOfflineSession(user) {
  if (!user?.id) return;
  await saveOfflineReference(user.id, OFFLINE_SESSION_NAME, {
    user,
    verifiedAt: Date.now(),
  });
}

export async function loadOfflineSession(now = Date.now()) {
  const userId = localStorage.getItem(ACTIVE_USER_KEY);
  if (!userId) return null;
  try {
    const cached = await loadOfflineReference(userId, OFFLINE_SESSION_NAME);
    const verifiedAt = Number(cached?.data?.verifiedAt || 0);
    if (!cached?.data?.user || now - verifiedAt > OFFLINE_SESSION_MAX_AGE_MS) return null;
    return cached.data.user;
  } catch {
    return null;
  }
}

function requestResult(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("Offline storage request failed"));
  });
}

async function userKey(db, userId) {
  const tx = db.transaction("meta", "readwrite");
  const store = tx.objectStore("meta");
  const id = `key:${userId}`;
  const existing = await requestResult(store.get(id));
  if (existing?.key) return existing.key;
  const key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
  await requestResult(store.put({ id, key, createdAt: new Date().toISOString() }));
  return key;
}

async function encryptJson(key, value) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const bytes = new TextEncoder().encode(JSON.stringify(value));
  const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, bytes);
  return { iv: Array.from(iv), ciphertext };
}

async function decryptJson(key, row) {
  const bytes = await crypto.subtle.decrypt({ name: "AES-GCM", iv: new Uint8Array(row.iv) }, key, row.ciphertext);
  return JSON.parse(new TextDecoder().decode(bytes));
}

export async function enqueueOperation(operation) {
  if (!operation?.operationId || !operation?.userId) throw new Error("Invalid offline operation");
  const db = await openDb();
  try {
    const key = await userKey(db, operation.userId);
    const encrypted = await encryptJson(key, { payload: operation.payload });
    const tx = db.transaction("operations", "readwrite");
    const store = tx.objectStore("operations");
    const existing = await requestResult(store.get(operation.operationId));
    if (existing && existing.userId !== operation.userId) throw new Error("Operation identity collision");
    const metadata = { ...operation };
    delete metadata.payload;
    await requestResult(store.put({ ...metadata, ...encrypted }));
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("ajfarm:sync-updated"));
    }
    return operation;
  } finally {
    db.close();
  }
}

export async function listOperations(userId) {
  const db = await openDb();
  try {
    const key = await userKey(db, userId);
    const tx = db.transaction("operations", "readonly");
    const rows = await requestResult(tx.objectStore("operations").index("userId").getAll(String(userId)));
    const decoded = [];
    for (const row of rows) {
      try {
        const secret = await decryptJson(key, row);
        decoded.push({ ...row, ...secret });
      } catch {
        decoded.push({ ...row, status: "review_required", errorCode: "LOCAL_DECRYPTION_FAILED", payload: null });
      }
    }
    return decoded.sort((a, b) => String(a.queuedAt).localeCompare(String(b.queuedAt)));
  } finally {
    db.close();
  }
}

async function saveOperationMetadata(operation) {
  const db = await openDb();
  try {
    const tx = db.transaction("operations", "readwrite");
    const store = tx.objectStore("operations");
    const row = await requestResult(store.get(operation.operationId));
    if (!row || row.userId !== operation.userId) return;
    const metadata = { ...operation };
    delete metadata.payload;
    await requestResult(store.put({ ...row, ...metadata, iv: row.iv, ciphertext: row.ciphertext }));
  } finally {
    db.close();
  }
}

async function performPendingSync({ userId, postOperation, uploadEvidence, online = isNetworkAvailable, now = () => Date.now() }) {
  if (!online()) return { attempted: 0, synced: 0, remaining: (await listOperations(userId)).filter((row) => row.status !== "synced").length };
  // A durable `syncing` row means a prior page/process was interrupted. Within
  // this page, activeSyncs prevents concurrent sends; after reload it is safe
  // and necessary to reclaim the same idempotent operation identity.
  const rows = (await listOperations(userId)).filter((row) => ["pending", "retrying", "syncing"].includes(row.status));
  let attempted = 0;
  let synced = 0;
  for (const row of rows) {
    if (Number(row.nextRetryAt || 0) > now()) continue;
    attempted += 1;
    row.status = "syncing";
    await saveOperationMetadata(row);
    try {
      let payload = { ...(row.payload || {}) };
      if ((row.evidenceIds || []).length) {
        const db = await openDb();
        let key;
        try { key = await userKey(db, row.userId); } finally { db.close(); }
        const evidence = await uploadRequiredEvidence(row, key, uploadEvidence);
        const first = evidence[row.evidenceIds[0]] || {};
        payload = {
          ...payload,
          photo_file_id: first.id || first.file_id,
          photo_filename: first.original_filename || first.filename,
          evidence_capture_timestamp: first.captured_at || null,
          required_evidence_ids: row.evidenceIds,
          photo_pending: false,
        };
      }
      const response = await postOperation({
        operation_id: row.operationId,
        device_id: row.deviceId,
        module: row.module,
        action: row.action,
        endpoint: row.endpoint,
        local_timestamp: row.localTimestamp,
        server_known_revision: row.serverKnownRevision,
        retry_count: row.retryCount,
        payload,
      });
      const data = response?.data || response || {};
      row.status = SAFE_STATUSES.has(data.status) ? data.status : "review_required";
      row.errorCode = data.error_code || null;
      row.acknowledgedAt = data.acknowledged_at || null;
      if (row.status === "synced") synced += 1;
    } catch (error) {
      const status = Number(error?.response?.status || 0);
      row.retryCount += 1;
      if ([400, 404, 409].includes(status)) {
        row.status = "review_required";
        row.errorCode = "REVIEW_REQUIRED";
      } else if ([401, 403].includes(status)) {
        row.status = "failed";
        row.errorCode = "AUTHORIZATION_CHANGED";
      } else {
        row.status = "retrying";
        row.errorCode = "TEMPORARY_CONNECTION_ERROR";
        row.nextRetryAt = now() + retryDelayMs(row.retryCount);
      }
    }
    await saveOperationMetadata(row);
  }
  const remaining = (await listOperations(userId)).filter((row) => row.status !== "synced").length;
  return { attempted, synced, remaining };
}

export function syncPendingOperations(options) {
  const userId = String(options?.userId || "");
  if (!userId) return Promise.reject(new Error("Authenticated user is required for synchronization"));
  const active = activeSyncs.get(userId);
  if (active) return active;
  const promise = performPendingSync(options).finally(() => {
    if (activeSyncs.get(userId) === promise) activeSyncs.delete(userId);
  });
  activeSyncs.set(userId, promise);
  return promise;
}

export async function syncSummary(userId) {
  const rows = await listOperations(userId);
  const db = await openDb();
  let evidenceRows = [];
  try {
    evidenceRows = await requestResult(db.transaction("evidence", "readonly").objectStore("evidence").index("userId").getAll(String(userId)));
  } finally {
    db.close();
  }
  const count = (status) => rows.filter((row) => row.status === status).length;
  const acknowledged = rows.filter((row) => row.acknowledgedAt).map((row) => row.acknowledgedAt).sort().pop() || null;
  return {
    total: rows.length,
    pending: rows.filter((row) => ["pending", "syncing", "retrying"].includes(row.status)).length,
    failed: count("failed"),
    reviewRequired: count("review_required"),
    pendingEvidence: evidenceRows.filter((row) => row.status !== "synced").length,
    synced: count("synced"),
    lastSuccessfulSync: acknowledged,
  };
}

export async function retryFailedOperations(userId) {
  const rows = await listOperations(userId);
  for (const row of rows.filter((item) => item.status === "failed")) {
    row.status = "retrying";
    row.errorCode = null;
    row.nextRetryAt = 0;
    await saveOperationMetadata(row);
  }
}

export async function clearOfflineDataForUser(userId) {
  if (!userId) return;
  try {
    if (!globalThis.indexedDB || !globalThis.crypto?.subtle) return;
    const db = await openDb();
    try {
    for (const storeName of ["operations", "evidence", "references"]) {
      const tx = db.transaction(storeName, "readwrite");
      const store = tx.objectStore(storeName);
      const rows = await requestResult(store.index("userId").getAllKeys(String(userId)));
      await Promise.all(rows.map((key) => requestResult(store.delete(key))));
    }
    await requestResult(db.transaction("meta", "readwrite").objectStore("meta").delete(`key:${userId}`));
    } finally { db.close(); }
  } finally {
    Object.keys(localStorage).filter((key) => key.startsWith("ajfarm:field-draft:") || key.startsWith("ajfarm:offline:") || key.startsWith("ajfarm:field-pending:")).forEach((key) => localStorage.removeItem(key));
    if (localStorage.getItem(ACTIVE_USER_KEY) === String(userId)) localStorage.removeItem(ACTIVE_USER_KEY);
  }
}

export function startAutomaticSync(runSync) {
  let stopped = false;
  let running = false;
  const run = async () => {
    if (stopped || running || !isNetworkAvailable()) return;
    running = true;
    try { await runSync(); } finally { running = false; }
  };
  window.addEventListener("online", run);
  const timer = window.setInterval(run, 5000);
  run();
  return () => {
    stopped = true;
    window.removeEventListener("online", run);
    window.clearInterval(timer);
  };
}
